/**
 * NOS-182: detected-form review actions — parse the import's
 * `content._detected_form` flag and perform the one-click conversion to the
 * EXISTING wired NOS blocks (form / newsletter_signup → submit-contact-form →
 * form_submissions, NOS-55/56/59) or dismiss it (keep the imported markup).
 *
 * Builders are pure (unit-tested); the convert/dismiss executors persist with
 * the same page_sections statements the customization store uses — no new
 * backend surface (mirrors importDecisionApply, NOS-214).
 */
import { supabase } from "@/lib/supabase"
import type { Json } from "@/types/database"

/** Mirrors DetectedFormField in supabase/functions/_shared/formIntent.ts. */
export interface DetectedFormFieldShape {
  name: string
  type: "text" | "email" | "textarea" | "select"
  label?: string
  required?: boolean
  options?: string[]
}

/** Mirrors DetectedForm in supabase/functions/_shared/formIntent.ts. */
export interface DetectedFormShape {
  heading?: string
  fields: DetectedFormFieldShape[]
  destination_email?: string
  submit_label?: string
  intent: "contact" | "newsletter"
}

const FIELD_TYPES = new Set(["text", "email", "textarea", "select"])

/**
 * The import's detected-form flag from a section's content, validated —
 * malformed/absent shapes return null (the section simply isn't flagged).
 */
export function parseDetectedForm(
  content: Record<string, unknown> | null | undefined
): DetectedFormShape | null {
  const raw = content?._detected_form
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) return null
  const rec = raw as Record<string, unknown>
  if (!Array.isArray(rec.fields)) return null
  const fields: DetectedFormFieldShape[] = []
  for (const f of rec.fields) {
    if (f == null || typeof f !== "object" || Array.isArray(f)) continue
    const fr = f as Record<string, unknown>
    if (typeof fr.name !== "string" || fr.name === "") continue
    if (typeof fr.type !== "string" || !FIELD_TYPES.has(fr.type)) continue
    fields.push({
      name: fr.name,
      type: fr.type as DetectedFormFieldShape["type"],
      ...(typeof fr.label === "string" && fr.label !== "" ? { label: fr.label } : {}),
      ...(fr.required === true ? { required: true } : {}),
      ...(Array.isArray(fr.options)
        ? { options: fr.options.filter((o): o is string => typeof o === "string") }
        : {}),
    })
  }
  if (fields.length === 0) return null
  return {
    ...(typeof rec.heading === "string" && rec.heading !== "" ? { heading: rec.heading } : {}),
    fields,
    ...(typeof rec.destination_email === "string" && rec.destination_email !== ""
      ? { destination_email: rec.destination_email }
      : {}),
    ...(typeof rec.submit_label === "string" && rec.submit_label !== ""
      ? { submit_label: rec.submit_label }
      : {}),
    intent: rec.intent === "newsletter" ? "newsletter" : "contact",
  }
}

/** Keys carried over from the imported section into the converted block. */
function carriedKeys(content: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (typeof content.import_source === "string") out.import_source = content.import_source
  if (content._styles != null && typeof content._styles === "object") {
    out._styles = content._styles
  }
  // Imported intro prose survives conversion — ContactFormView renders body.
  const body = typeof content.body === "string" ? content.body : undefined
  if (body && body.trim() !== "") out.body = body
  return out
}

const NAME_FIELD_RE = /(^|[^a-z])(name|full[ _-]?name)([^a-z]|$)/i

/**
 * Contact-block content prefilled from the detection. submit-contact-form
 * REQUIRES form_data.email (and uses form_data.name for the contact upsert),
 * so the first email-type field is canonicalized to name "email" (forced
 * required) and the first name-ish text field to "name" — a form without any
 * email field gains one, or submissions could never be delivered.
 */
export function buildContactFormContent(
  content: Record<string, unknown>,
  detected: DetectedFormShape
): Record<string, unknown> {
  const fields: DetectedFormFieldShape[] = []
  const used = new Set<string>()
  let emailDone = false
  let nameDone = false
  for (const f of detected.fields) {
    let name = f.name
    if (!emailDone && f.type === "email") {
      name = "email"
      emailDone = true
    } else if (
      !nameDone &&
      f.type === "text" &&
      NAME_FIELD_RE.test(`${f.name} ${f.label ?? ""}`)
    ) {
      name = "name"
      nameDone = true
    }
    let unique = name
    let n = 2
    while (used.has(unique)) unique = `${name}_${n++}`
    used.add(unique)
    fields.push({
      ...f,
      name: unique,
      ...(unique === "email" ? { required: true } : {}),
    })
  }
  if (!emailDone) {
    // Insert after the leading (usually name) field — Name, Email, … order.
    const at = Math.min(1, fields.length)
    fields.splice(at, 0, { name: "email", type: "email", label: "Email", required: true })
  }
  return {
    ...carriedKeys(content),
    heading:
      detected.heading ??
      (typeof content.heading === "string" && content.heading !== ""
        ? content.heading
        : "Get in touch"),
    submit_label: detected.submit_label ?? "Send message",
    fields,
  }
}

/** Newsletter-signup content prefilled from a newsletter-intent detection. */
export function buildNewsletterContent(
  content: Record<string, unknown>,
  detected: DetectedFormShape
): Record<string, unknown> {
  const emailField = detected.fields.find((f) => f.type === "email")
  return {
    ...carriedKeys(content),
    heading:
      detected.heading ??
      (typeof content.heading === "string" && content.heading !== ""
        ? content.heading
        : "Subscribe to our newsletter"),
    cta_text: detected.submit_label ?? "Subscribe",
    placeholder: emailField?.label ?? "Enter your email",
  }
}

export interface ConvertResult {
  section_type: "form" | "newsletter_signup"
  content: Record<string, unknown>
}

/**
 * One-click convert: the flagged section BECOMES the wired NOS block for its
 * intent, prefilled with the detected fields — submissions land in the
 * existing form-submissions inbox. Same persistence statement the store uses.
 */
export async function convertDetectedForm(
  sectionId: string,
  content: Record<string, unknown>,
  detected: DetectedFormShape
): Promise<ConvertResult> {
  const result: ConvertResult =
    detected.intent === "newsletter"
      ? { section_type: "newsletter_signup", content: buildNewsletterContent(content, detected) }
      : { section_type: "form", content: buildContactFormContent(content, detected) }
  const { error } = await supabase
    .from("page_sections")
    .update({ section_type: result.section_type, content: result.content as Json })
    .eq("id", sectionId)
  if (error) throw new Error(error.message)
  return result
}

/**
 * Dismiss: some "forms" are false positives (newsletter embeds, third-party
 * widgets) — clear the flag, keep the imported markup exactly as-is.
 */
export async function dismissDetectedForm(
  sectionId: string,
  content: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const next = { ...content }
  delete next._detected_form
  const { error } = await supabase
    .from("page_sections")
    .update({ content: next as Json })
    .eq("id", sectionId)
  if (error) throw new Error(error.message)
  return next
}
